// Pure draft/step model for CreatePurchaseOrderModal, exercised without React.
// The line items are the anchor: patient and product are read off them, never chosen.

export const CREATE_PO_STEPS = ['details', 'lines', 'order'] as const;
export type CreatePurchaseOrderStep = (typeof CREATE_PO_STEPS)[number];

export interface CreatePurchaseOrderDraft {
    practiceId: string | null;
    chargeId: string | null;
    chargeLineItemIds: string[];
    vendorId: string | null;
    orderMethodId: string | null;
    poNumber: string;
    // A <input type="datetime-local"> value (local time, no offset) - the
    // order was placed before it's being recorded, so this can be backdated.
    sentAt: string;
}

export function toDatetimeLocalValue(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function emptyPurchaseOrderDraft(
    initialPracticeId?: string | null
): CreatePurchaseOrderDraft {
    return {
        practiceId: initialPracticeId ?? null,
        chargeId: null,
        chargeLineItemIds: [],
        vendorId: null,
        orderMethodId: null,
        poNumber: '',
        sentAt: toDatetimeLocalValue(new Date())
    };
}

// An emptied <input type="datetime-local"> yields '', which new Date('')
// turns into an Invalid Date whose toISOString() throws on submit.
function isDateTimeLocalValid(value: string): boolean {
    return value.length > 0 && !Number.isNaN(Date.parse(value));
}

export function isStepComplete(
    draft: CreatePurchaseOrderDraft,
    step: CreatePurchaseOrderStep
): boolean {
    if (step === 'details') return draft.practiceId !== null && draft.chargeId !== null;
    if (step === 'lines') return draft.chargeLineItemIds.length > 0;
    return (
        draft.vendorId !== null &&
        draft.orderMethodId !== null &&
        isDateTimeLocalValid(draft.sentAt)
    );
}

export function isDraftComplete(draft: CreatePurchaseOrderDraft): boolean {
    return CREATE_PO_STEPS.every((step) => isStepComplete(draft, step));
}
